import { useEffect } from "react";
import { FibonacciSpiral } from "./FibonacciSpiral";

export function PrivacySecurity({
  returnLabel,
  onReturn,
}: {
  returnLabel: string;
  onReturn: () => void;
}) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Privacy and security · Feel Scene";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main className="privacy-shell">
      <FibonacciSpiral className="privacy-spiral" />
      <header className="privacy-header">
        <a
          className="wordmark privacy-wordmark"
          href="/"
          onClick={(event) => {
            event.preventDefault();
            onReturn();
          }}
        >
          Feel Scene
        </a>
        <button className="text-button" type="button" onClick={onReturn}>
          ← {returnLabel}
        </button>
      </header>

      <article className="privacy-content">
        <header className="privacy-hero">
          <span className="eyebrow">Privacy and security</span>
          <h1>Sensitive by design. Honest about the limits.</h1>
          <p>
            Feel Scene reduces how much sensitive content leaves your device,
            but no website can make a shared secret risk-free. A complete room
            link is a key. Anyone who can use that link, your unlocked browser,
            or an export may be able to read or change private content.
          </p>
        </header>

        <section aria-labelledby="short-version">
          <h2 id="short-version">The short version</h2>
          <div className="privacy-summary-grid">
            <article className="privacy-summary-card">
              <h3>Sharing one device</h3>
              <p>
                Saved session content stays in that browser and is encrypted
                with a non-exportable browser key. It is not a password-protected
                vault. It clears when you choose Clear session or after 30 days
                by default; the Room menu can keep the one current session
                on this device indefinitely.
              </p>
            </article>
            <article className="privacy-summary-card">
              <h3>Using a remote room</h3>
              <p>
                Names, answers, personal text, shared notes, and custom terms are
                encrypted in the browser. The room service stores ciphertext and
                deletes it 30 days after creation or the latest explicit
                extension, or when either seat deletes the room.
              </p>
            </article>
            <article className="privacy-summary-card">
              <h3>Hidden until both save</h3>
              <p>
                The service withholds a partner’s encrypted answer until both
                seats have saved that item. This supports an honest conversation;
                it cannot stop someone who controls both links or modifies their
                browser client.
              </p>
            </article>
            <article className="privacy-summary-card">
              <h3>No account or trackers</h3>
              <p>
                There are no accounts, ads, analytics, public room directory, or
                third-party tracking scripts. Hosting still processes ordinary
                network and operational metadata such as IP addresses and timing.
              </p>
            </article>
          </div>
        </section>

        <section className="privacy-practical" aria-labelledby="protect-yourself">
          <h2 id="protect-yourself">Protect this conversation</h2>
          <ol>
            <li>
              Send the complete invite through a trusted, end-to-end encrypted
              channel. Do not post it or forward it casually.
            </li>
            <li>
              Use a device and browser profile you trust. Extensions, malware,
              other device users, clipboard history, and browser sync are outside
              Feel Scene’s control.
            </li>
            <li>
              Delete the room when you are finished. Review downloads before
              sharing them; exported files do not expire with the room.
            </li>
            <li>
              Treat answers as a conversation aid, never as standing consent or
              proof of identity.
            </li>
          </ol>
        </section>

        <section className="privacy-technical" aria-labelledby="technical-detail">
          <div>
            <span className="eyebrow">Expandable detail</span>
            <h2 id="technical-detail">How it works, strengths and risks</h2>
          </div>

          <details>
            <summary>What is stored, and where?</summary>
            <div className="privacy-detail-body">
              <h3>Local mode</h3>
              <p>
                The current saved session is encrypted with AES-256-GCM before
                it is persisted in this origin’s IndexedDB. Its randomly generated
                key is stored there as a non-extractable Web Crypto key, so simple
                inspection or copying of the stored record does not reveal the
                session. Unsaved text drafts use per-tab session storage and are
                outside this at-rest layer. Local content is not sent to the room
                API.
              </p>
              <p>
                Non-extractable does not mean inaccessible to the app. Code running
                with this origin’s permissions can ask the key to decrypt records,
                and an unlocked browser, malicious extension, compromised device,
                or altered site code can still expose them. There is no user
                password or hardware-backed identity protecting the key.
                If a browser blocks IndexedDB or stored Web Crypto keys, the app
                falls back to its expiring legacy browser record so recovery still
                works; that fallback is not encrypted at rest.
              </p>
              <h3>Remote mode</h3>
              <p>
                A SQLite-backed Durable Object stores encrypted participant
                profiles, answers, contexts, shared notes, and custom
                entries. It also stores the minimum state needed to authorise
                seats, coordinate reveal, resolve versions, reconnect clients,
                and expire the room.
              </p>
              <h3>Browser recovery and exports</h3>
              <p>
                The browser keeps navigation preferences and, for the creator,
                a browser-key-encrypted partner invite tagged with the room expiry
                so it can be copied again. It is ignored and removed when the app
                next runs after that expiry. Room deletion or observed expiry clears
                room-specific drafts, navigation state, and that saved invite from
                the current browser.
                Browser history, sync, backups, clipboard managers, messages,
                screenshots, printouts, and downloaded exports are separate copies
                and are not remotely erased.
              </p>
            </div>
          </details>

          <details>
            <summary>Encryption and room links</summary>
            <div className="privacy-detail-body">
              <p>
                Remote content uses the browser’s Web Crypto implementation of
                AES-256-GCM with a fresh random 96-bit nonce for each encrypted
                value. Authenticated associated data binds ciphertext to its room
                and, as applicable, its content kind, seat, and item. Room
                identifiers use 144 random bits; room keys and seat capabilities
                use 256 random bits.
              </p>
              <p>
                The separate browser-at-rest layer uses another randomly generated
                AES-256-GCM key. That key is marked non-extractable and stored as a
                CryptoKey in IndexedDB. Record identifiers and expiry times remain
                metadata; authenticated associated data prevents changing them
                without invalidating the ciphertext. This mainly protects against
                casual or offline inspection, not code already executing as Feel
                Scene in the browser.
              </p>
              <p>
                The decryption key and seat capability are carried in the URL
                fragment, which browsers do not include in ordinary HTTP requests
                or referrer headers. The room key stays in the browser. The seat
                capability is sent separately in an authorisation header and
                WebSocket protocol during room access; the service stores only its
                SHA-256 hash.
              </p>
              <p>
                This limits the value of a room-service database leak: without a
                complete link, stored content remains ciphertext. It does not
                protect against a compromised device, malicious browser extension,
                stolen full link, or a compromised site deployment that serves
                altered client code. Both participants share one room key, and
                this version has no key rotation or forward secrecy.
              </p>
            </div>
          </details>

          <details>
            <summary>What the service and each participant can learn</summary>
            <div className="privacy-detail-body privacy-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Party</th>
                    <th>Visible information</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Hosting and room service</th>
                    <td>
                      Network metadata, random room ID, creation and expiry times,
                      catalogue version, hashed seat credentials, ciphertext sizes
                      and timing, connection state, canonical item identifiers,
                      presence, and which seats have saved an item. It cannot read
                      encrypted names, choices, personal text, notes, or custom-term
                      wording without the room key.
                    </td>
                  </tr>
                  <tr>
                    <th>Partner before reveal</th>
                    <td>
                      Participant names and defaults, shared notes and bookmarks,
                      shared custom-term metadata, presence, and whether the other
                      seat has saved an item. Hidden answer values and personal text
                      are not delivered yet.
                    </td>
                  </tr>
                  <tr>
                    <th>Partner after reveal</th>
                    <td>
                      Both saved structured answers and any saved personal context
                      and Important marker for that item. Later saved edits
                      are visible and marked as changed.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <details>
            <summary>Seat ownership and mutual reveal limits</summary>
            <div className="privacy-detail-body">
              <p>
                Each link has a separate bearer capability. The room service binds
                a connection to one seat, prevents that seat from writing the other
                seat’s answers or profile, and never sends the other seat’s answer
                ciphertext until the receiving seat has saved the same item.
                Creator-only custom-entry edits are enforced by the service.
              </p>
              <p>
                Encryption prevents the service from checking whether a saved
                answer is sincere or even well-formed. A participant using a
                modified client can submit an encrypted placeholder to trigger
                reveal. The creator also handles both seat links while sending the
                invite and could retain or use the partner link. There is no account,
                identity proof, individual link rotation or revocation, or protection
                from a participant copying revealed content. Either valid seat can
                delete the whole room and revoke both seats.
              </p>
            </div>
          </details>

          <details>
            <summary>Deletion, expiry and recovery</summary>
            <div className="privacy-detail-body">
              <p>
                Remote expiry starts at 30 days from creation. Either seat can
                explicitly reset it to 30 days from that moment in the Room menu;
                ordinary activity does not extend it. A server alarm deletes
                application-level room storage and disconnects live clients. Encrypted
                local records expire after 30 days by default and are removed by
                Clear session or when the app next loads an expired local session.
                Choosing indefinite retention keeps the one encrypted local record
                until it is cleared or browser storage is removed.
              </p>
              <p>
                Taking a local session online creates a separate 30-day encrypted
                room. Copying one side excludes the other participant’s responses and
                shared notes. Copying both sides includes both saved response sets and
                shared notes. The local source remains on the device under its chosen
                retention setting.
              </p>
              <p>
                Deletion is deliberately irreversible and there is no account-based
                recovery or server-generated backup. It does not retract content a
                participant already saw or copied, nor does it control browser or
                infrastructure-level logs and backups governed by the device owner
                or hosting provider’s retention practices.
              </p>
            </div>
          </details>

          <details>
            <summary>Browser and application security controls</summary>
            <div className="privacy-detail-body">
              <p>
                User-supplied text is rendered as text through React rather than as
                HTML. A Content Security Policy restricts scripts, styles,
                connections, images, fonts, forms, and base URLs to the app’s own
                origin where needed. Framing is blocked, referrers are suppressed,
                MIME sniffing is disabled, powerful browser features are denied, and
                cross-origin opener and resource policies reduce accidental data
                exposure.
              </p>
              <p>
                These are defence-in-depth controls, not a guarantee against future
                software defects or supply-chain compromise. The design and
                implementation have not been represented as an independent security
                audit or formal cryptographic verification.
              </p>
            </div>
          </details>

          <details>
            <summary>Availability, abuse and denial-of-wallet controls</summary>
            <div className="privacy-detail-body">
              <p>
                Static files bypass Worker execution. Room creation and access are
                edge-rate-limited before Durable Objects are invoked. Request,
                ciphertext, row, custom-entry, message-rate, and live-connection
                bounds limit storage and compute amplification. Empty storage is not
                initialised by random room probes. Fixed expiry removes abandoned
                rooms, and hibernatable WebSockets avoid billable active duration
                while rooms are idle.
              </p>
              <p>
                These controls reduce ordinary abuse and financial risk; they cannot
                eliminate a distributed attack, traffic from many networks, abuse by
                someone holding a valid room link, a Cloudflare outage, or an unknown
                platform vulnerability. Rate limits are intentionally best-effort and
                may temporarily reject legitimate bursts. Deployment owners still
                need provider-side usage alerts, spending controls, and incident
                response.
              </p>
            </div>
          </details>
        </section>
      </article>
    </main>
  );
}
